import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const aggregators = [
  { name: 'Zomato',   logo: '/logo-zomato.png',   tag: 'Menu Sync',        tagColor: '#e53935', desc: 'Direct order injection and instant menu updates across all locations.' },
  { name: 'Swiggy',   logo: '/logo-swiggy.png',   tag: 'Orders Injection', tagColor: '#f97316', desc: 'Real-time status tracking and seamless inventory mapping to prevent stockouts.' },
  { name: 'Magicpin', logo: '/logo-magicpin.png',  tag: 'Reconciliation',   tagColor: '#8b5cf6', desc: 'Streamlined voucher redemption and automated reconciliation for every order.' },
];

const payments = [
  { name: 'Razorpay',      logo: '/logo-razorpay.png', desc: 'Fast and secure settlements with unified payment reporting.' },
  { name: 'Pine Labs',     logo: '/logo-pinelabs.png', desc: 'Seamless terminal integration for high-speed offline checkouts.' },
  { name: 'Paytm',         logo: '/logo-paytm.png',    desc: 'Instant QR payments with real-time payment status updates.' },
  { name: 'UPI Providers', logo: '/logo-upi.png',      desc: 'Zero-MDR flows via GPay, PhonePe, and BharatPe QR.' },
];

const posFeatures = [
  { icon: '🖨️', title: 'Cost Center-wise KOT Printing', desc: 'Assign a unique printer to multiple cost centers and send KOTs to respective cost centers. Sync every KOT with the master POS to track all orders.' },
  { icon: '🪑', title: 'Table & Area Management',       desc: 'Configure different dine-in areas with their menus, pricing, tax rates and customizable seating. Track vacancies, steward assignments and table-wise orders.' },
  { icon: '🖥️', title: 'Multi Billing Terminals',       desc: 'Create multiple billing counters synced with one order terminal so captains can generate KOTs and cashiers settle bills from any counter.' },
  { icon: '🧾', title: 'Customizable Bill Format',      desc: 'Print your restaurant logo, bill break-ups, customer details or a dynamic QR code for payments right at the billing counter.' },
  { icon: '💰', title: 'Configure Taxes & Discounts',   desc: 'Easily configure and levy different taxes, update tax rates and offer discounts depending on your service types, regional norms and business needs.' },
  { icon: '⚡', title: 'Uninterrupted Billing',         desc: 'Experience smooth checkout with tablet, mobile, and desktop-based Touch POS systems that ensure smooth checkout, even offline.' },
];

const mmsCloud = [
  { icon: '💻', title: 'Any Laptop or Desktop',     desc: 'Access it from any laptop or desktop — no dedicated hardware required.' },
  { icon: '☁️', title: 'Cloud Anytime, Anywhere',   desc: 'Your data is on cloud — access anytime, anywhere with up to 99.9% uptime.' },
  { icon: '🔒', title: 'Safe & Secure',              desc: 'Industry-standard encryption prevents even the remotest chance of fraud or mismanagement.' },
  { icon: '💳', title: 'Simple Monthly Payment',    desc: 'No license fees, no upgradation required — ever. Simple monthly model.' },
  { icon: '🚫', title: 'No Server Issues',           desc: 'No software updates, no server hassles. All your data is in the cloud, no expensive hardware.' },
  { icon: '⚡', title: 'Real-Time Data',             desc: 'All your data is real time regardless of location or time. Unparalleled efficiency across departments.' },
];

const mmsFeatures = [
  'Manage end-to-end inventory requirements',
  'Recipe management including Yield & Semi-Finished goods',
  'Role-based security & new user interface',
  'Advanced multi-level access authentication',
  'Authorization mails sent to predefined email IDs',
  'Export all reports to MS Excel, Word and more',
  'Direct communication with DPOS Inventory',
  'Backdated entries to recalculate monthly costings',
  'Real-time online reports',
  'Interfaced with Tally and other AR software',
];

function LogoImg({ src, name }) {
  const [err, setErr] = useState(false);
  return (
    <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
      {!err
        ? <img src={src} alt={name} onError={() => setErr(true)} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        : <span style={{ fontSize: 14, fontWeight: 800, color: '#6b7280' }}>{name[0]}</span>}
    </div>
  );
}

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'pos' | 'int' | null
  const location = useLocation();
  const dropRef = useRef();

  useEffect(() => { setMenuOpen(false); setModal(null); }, [location]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const closeModal = () => setModal(null);

  return (
    <>
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/logo-header.png" alt="DPOS" style={{ height: 48, display: 'block' }} />
          </Link>

          {/* Centre nav links */}
          <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {[
              { label: 'POS',             to: '/pos' },
              { label: 'Integrations',    to: '/integrations' },
              { label: 'Online Ordering', to: '/online-ordering' },
              { label: 'Inventory',       to: '/material-management' },
            ].map(item => (
              <Link key={item.label} to={item.to} style={{ fontSize: 14, fontWeight: 500, color: '#111827', padding: '8px 14px', borderRadius: 8, textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#CC0000'; e.currentTarget.style.background = 'rgba(204,0,0,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#111827'; e.currentTarget.style.background = 'transparent'; }}>{item.label}</Link>
            ))}
          </div>

          {/* Right: hamburger (Contact + About only) */}
          <button
            ref={dropRef}
            onClick={() => { setMenuOpen(o => !o); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 5, padding: 8, borderRadius: 8, position: 'relative' }}
            aria-label="Toggle menu"
          >
            <span style={{ display: 'block', width: 24, height: 2, background: '#111827', borderRadius: 2, transition: 'all 0.3s', transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none' }} />
            <span style={{ display: 'block', width: 24, height: 2, background: '#111827', borderRadius: 2, transition: 'all 0.3s', opacity: menuOpen ? 0 : 1 }} />
            <span style={{ display: 'block', width: 24, height: 2, background: '#111827', borderRadius: 2, transition: 'all 0.3s', transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none' }} />

            {menuOpen && (
              <div onClick={e => e.stopPropagation()} style={{
                position: 'absolute', top: 44, right: 0,
                background: '#fff', borderRadius: 12,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                border: '1px solid #e5e7eb',
                display: 'flex', flexDirection: 'column',
                minWidth: 220, overflow: 'hidden', zIndex: 60,
              }}>
                {/* Mobile-only: main nav pages */}
                {[
                  { label: 'POS',             to: '/pos' },
                  { label: 'Integrations',    to: '/integrations' },
                  { label: 'Online Ordering', to: '/online-ordering' },
                  { label: 'Inventory',       to: '/material-management' },
                ].map(item => (
                  <Link key={item.label} to={item.to} className="mobile-menu-item" style={{ fontSize: 15, fontWeight: 500, color: '#374151', padding: '13px 24px', borderBottom: '1px solid #f3f4f6', textAlign: 'left', display: 'block' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#CC0000'}
                    onMouseLeave={e => e.currentTarget.style.color = '#374151'}>{item.label}</Link>
                ))}
                {/* Always: Contact + About */}
                <Link to="/contact" style={{ fontSize: 15, fontWeight: 500, color: '#374151', padding: '13px 24px', borderBottom: '1px solid #f3f4f6', textAlign: 'left', display: 'block' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#CC0000'}
                  onMouseLeave={e => e.currentTarget.style.color = '#374151'}>Contact Us</Link>
                <Link to="/about" style={{ fontSize: 15, fontWeight: 500, color: '#374151', padding: '13px 24px', borderBottom: '1px solid #f3f4f6', textAlign: 'left', display: 'block' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#CC0000'}
                  onMouseLeave={e => e.currentTarget.style.color = '#374151'}>About Us</Link>
                <Link to="/blog" style={{ fontSize: 15, fontWeight: 500, color: '#374151', padding: '13px 24px', borderBottom: '1px solid #f3f4f6', textAlign: 'left', display: 'block' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#CC0000'}
                  onMouseLeave={e => e.currentTarget.style.color = '#374151'}>Blog</Link>
                <div style={{ padding: '14px 24px' }}>
                  <Link to="/contact" style={{ background: '#CC0000', color: '#fff', padding: '9px 20px', borderRadius: 8, fontWeight: 600, fontSize: 14, display: 'inline-block' }}>Contact for Free Demo</Link>
                </div>
              </div>
            )}
          </button>
        </div>
      </nav>

      {/* ── POS Modal ───────────────────────────────────── */}
      {modal === 'pos' && (
        <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, boxShadow: '0 24px 80px rgba(0,0,0,0.2)', width: '100%', maxWidth: 900, maxHeight: '90vh', overflowY: 'auto' }}>
            {/* Header */}
            <div style={{ padding: '16px 28px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Restaurant POS</span>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>

            {/* Hero: image + text */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              <div style={{ padding: '32px 28px' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Restaurant POS</span>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1.3, margin: '10px 0 14px' }}>
                  Restaurant Billing Software — Transform to the Digital World
                </h2>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, marginBottom: 20 }}>
                  Grow your business using technology integrated online/offline Restaurant POS systems. Manages all your restaurant operations efficiently so that you can focus on improving the day-to-day operations of your business.
                </p>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, marginBottom: 24 }}>
                  Multiple ways to satisfy customers by offering QR code tableside ordering, delivery, takeaway, payments and many more.
                </p>
                <Link to="/pos" onClick={closeModal} style={{ background: '#CC0000', color: '#fff', padding: '10px 22px', borderRadius: 8, fontWeight: 600, fontSize: 14, display: 'inline-block' }}>
                  Explore POS →
                </Link>
              </div>
              <div style={{ overflow: 'hidden', borderRadius: '0 20px 0 0', minHeight: 320 }}>
                <img src="/pos-touch.webp" alt="POS System" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
              </div>
            </div>

            {/* Features */}
            <div style={{ padding: '28px 28px 32px', borderTop: '1px solid #f3f4f6' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>All-rounder Restaurant Billing Software</p>
              <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 20 }}>Everything you need to run your restaurant smoothly</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                {posFeatures.map(f => (
                  <div key={f.title} style={{ background: '#f9fafb', borderRadius: 12, padding: '18px 16px', border: '1px solid #e5e7eb', transition: 'all 0.2s', cursor: 'default' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#CC0000'; e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(204,0,0,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ fontSize: 24, marginBottom: 10 }}>{f.icon}</div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 6 }}>{f.title}</p>
                    <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Inventory / MMS Modal ───────────────────────── */}
      {modal === 'inv' && (
        <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, boxShadow: '0 24px 80px rgba(0,0,0,0.2)', width: '100%', maxWidth: 920, maxHeight: '90vh', overflowY: 'auto' }}>

            {/* Header */}
            <div style={{ padding: '16px 28px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Materials Management System</span>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>

            {/* Hero: text only */}
            <div style={{ padding: '32px 28px' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.1em' }}>DGPOS ERP</span>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1.3, margin: '10px 0 14px' }}>
                Material Management System
              </h2>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, marginBottom: 12 }}>
                A web-based application that allows a company or brand to manage activities efficiently. As the features are biased towards F&B operations, MMS is apt for hotels and restaurants.
              </p>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, marginBottom: 24 }}>
                Right from indent to purchase, to receiving and finally tracking stock inventory — manage all major functions efficiently. Supports multi-location operations.
              </p>
              <Link to="/material-management" onClick={closeModal} style={{ background: '#CC0000', color: '#fff', padding: '10px 22px', borderRadius: 8, fontWeight: 600, fontSize: 14, display: 'inline-block' }}>
                Explore MMS →
              </Link>
            </div>

            {/* Anytime Anywhere section */}
            <div style={{ padding: '28px 28px 0', borderTop: '1px solid #f3f4f6' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Anytime, Anywhere — DPOS Inventory</p>
              <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 20 }}>Cloud-powered, always accessible, always secure</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
                {mmsCloud.map(f => (
                  <div key={f.title} style={{ background: '#f9fafb', borderRadius: 12, padding: '16px 14px', border: '1px solid #e5e7eb', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#CC0000'; e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(204,0,0,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ fontSize: 22, marginBottom: 8 }}>{f.icon}</div>
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 4 }}>{f.title}</p>
                    <p style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Features list */}
            <div style={{ padding: '24px 28px 32px' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 16 }}>DPOS Inventory Features</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px' }}>
                {mmsFeatures.map(f => (
                  <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span style={{ color: '#CC0000', fontWeight: 700, fontSize: 14, flexShrink: 0, marginTop: 1 }}>✓</span>
                    <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Integrations Modal ──────────────────────────── */}
      {modal === 'int' && (
        <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, boxShadow: '0 24px 80px rgba(0,0,0,0.2)', width: '100%', maxWidth: 860, overflow: 'hidden' }}>
            <div style={{ padding: '16px 28px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Integrations</span>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 72px 1fr' }}>
              <div style={{ padding: '24px 28px' }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Food Aggregators</p>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 18, lineHeight: 1.6 }}>Automate online orders with direct injection. Manage Zomato, Swiggy, and Magicpin from your POS.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {aggregators.map(item => (
                    <div key={item.name} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <LogoImg src={item.logo} name={item.name} />
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{item.name}</span>
                          <span style={{ fontSize: 10, fontWeight: 600, background: item.tagColor + '18', color: item.tagColor, padding: '2px 8px', borderRadius: 100 }}>{item.tag}</span>
                        </div>
                        <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.55 }}>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderLeft: '1px solid #f3f4f6', borderRight: '1px solid #f3f4f6' }}>
                <div style={{ flex: 1, width: 1, background: 'linear-gradient(to bottom, transparent, #fecaca)' }} />
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fef2f2', border: '2px solid #fecaca', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                  <img src="/logo-d.png" alt="D" style={{ width: 56, height: 56, objectFit: 'contain', margin: 4 }} />
                </div>
                <div style={{ flex: 1, width: 1, background: 'linear-gradient(to bottom, #fecaca, transparent)' }} />
              </div>

              <div style={{ padding: '24px 28px' }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Payment Gateways</p>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 18, lineHeight: 1.6 }}>Accept payments securely with trusted providers. Automated settlement mapping keeps your accounts accurate.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {payments.map(item => (
                    <div key={item.name} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <LogoImg src={item.logo} name={item.name} />
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', display: 'block', marginBottom: 3 }}>{item.name}</span>
                        <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.55 }}>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ padding: '14px 28px', borderTop: '1px solid #f3f4f6', background: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>Seamlessly connected to India's top platforms</span>
              <Link to="/integrations" onClick={closeModal} style={{ fontSize: 13, fontWeight: 600, color: '#CC0000' }}>See all integrations →</Link>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
